import { Box, IconButton, Divider, Avatar, Typography, TextField, lighten, Tooltip, Slide } from "@mui/material";
import {
    ArrowBack,
    ArrowBackIosNew,
    ArrowForwardIos,
    Comment,
    Favorite,
    FavoriteBorder,
    Launch,
    Send,
    Subtitles,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";
import { useMemo, useState, useCallback } from "react";
import Theme from "../utils/theme";
import axiosService from "../utils/axios";
import type { RootState } from "../store";
import Comments from "./Comments";
import type { IImage } from "../types/image";
import { scrollbarCss } from "../utils/themeScrollbar";
import type { IComment } from "../types/comment";
import { useSwipeNavigation } from "../hooks/useSwipeNavigation";

interface IImageBoxProps {
    link?: string[];
    images: IImage[];
    setImages?: (type: "likes" | "commentText" | "commentLike", comment?: IComment, commentId?: string) => void;
    currentImageIndex: number;
    setCurrentImageIndex: (index: number) => void;
}

const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

const ImageBox: React.FC<IImageBoxProps> = ({ link = [], images, currentImageIndex, setCurrentImageIndex, setImages = undefined }) => {
    const themeState = useTypedSelector((state) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwipeNavigation({
        currentIndex: currentImageIndex,
        maxIndex: images.length - 1,
        onChange: setCurrentImageIndex,
    });

    const authState = useSelector((state: RootState) => state.auth);
    const isGuest = !authState.account;

    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [openComments, setOpenComments] = useState(false);
    const [openCaptions, setOpenCaptions] = useState(false);
    const [imageLikes, setImageLikes] = useState<string[][]>(images.map((image) => image.likes));

    const handleLikeImage = useCallback(() => {
        if (isGuest) return;

        setImageLikes((prev) => {
            const newLikes = [...prev.map((subarray: string[]) => [...subarray])];
            const currentLikes = newLikes[currentImageIndex];

            if (currentLikes.includes(authState.account!.username))
                newLikes[currentImageIndex] = currentLikes.filter((name: string) => name !== authState.account!.username);
            else currentLikes.push(authState.account!.username);

            return newLikes;
        });

        if (setImages) setImages("likes");

        axiosService.post(`${process.env.REACT_APP_API_URL}/images/like`, { imageId: images[currentImageIndex]._id }).then((res) => {});
    }, [authState.account, currentImageIndex, images, isGuest, setImages]);

    const handleSendMessage = useCallback(() => {
        if (!message.trim() || loading || !authState.account) return;

        setLoading(true);
        axiosService
            .post(`${process.env.REACT_APP_API_URL}/images/comment/create`, {
                content: message,
                refId: images[currentImageIndex]["_id"],
            })
            .then((res) => {
                if (setImages)
                    setImages("commentText", {
                        ...res.data.body,
                        user: {
                            username: authState.account!.username,
                            image: { url: authState.account!.image },
                        },
                    } as IComment);
            });
    }, [message, loading, authState.account, images, currentImageIndex, setImages]);

    return (
        <Box
            sx={{
                height: { xs: "100dvh", sm: "80dvh" },
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
                boxSizing: "border-box",
            }}
        >
            {/* Inner content wrapper with max width */}
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 1200,
                    display: "flex",
                    backgroundColor: "#f0f0f0",
                    height: "100%",
                    overflow: "hidden",
                }}
            >
                {/* Image section */}
                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        right: 0,
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 0,
                        minHeight: 0,
                        height: "100%",
                        boxSizing: "border-box",
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {/* Left arrow */}
                    {currentImageIndex > 0 && (
                        <IconButton
                            onClick={() => setCurrentImageIndex(currentImageIndex - 1)}
                            sx={{
                                ml: 1,
                                position: "absolute",
                                left: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 4,

                                backgroundColor: "rgba(255, 255, 255, 0.51)",
                                backdropFilter: "blur(40px)",
                                WebkitBackdropFilter: "blur(40px)",
                                borderColor: "rgba(255, 255, 255, 0.4)",

                                "&:hover": {
                                    background: "rgba(255,255,255,0.7)",
                                },
                            }}
                        >
                            <ArrowBackIosNew sx={{ color: "#C2410C" }} />
                        </IconButton>
                    )}
                    {/* Right arrow */}
                    {currentImageIndex < images.length - 1 && (
                        <IconButton
                            onClick={() => setCurrentImageIndex(currentImageIndex + 1)}
                            sx={{
                                mr: 1,
                                position: "absolute",
                                right: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 4,

                                backgroundColor: "rgba(255, 255, 255, 0.51)",
                                backdropFilter: "blur(40px)",
                                WebkitBackdropFilter: "blur(40px)",
                                borderColor: "rgba(255, 255, 255, 0.4)",

                                "&:hover": {
                                    background: "rgba(255,255,255,0.7)",
                                },
                            }}
                        >
                            <ArrowForwardIos sx={{ color: "#C2410C" }} />
                        </IconButton>
                    )}
                    {/* The image itself */}
                    <Box
                        component="img"
                        src={images[currentImageIndex].url}
                        alt="modal image"
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            display: "block",
                        }}
                    />
                    {/* Image Box Controls */}
                    <Box
                        sx={{
                            display: { xs: openComments ? "none" : "flex", sm: "flex" },
                            position: "absolute",
                            left: { sm: 5 },
                            top: 5,
                            p: 1,
                            border: "2px solid " + theme.palette.primary.main,
                            borderRadius: 5,
                            flexDirection: { xs: "row", sm: "column" },
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            backdropFilter: "blur(40px)",
                            WebkitBackdropFilter: "blur(40px)",
                            borderColor: "rgba(255, 255, 255, 0.4)",
                            gap: 1,
                        }}
                    >
                        {link.length > 0 && (
                            <Tooltip title="Open Recipe" placement="right">
                                {/* sx={{ ml: 1, mr: 1 }} */}
                                <IconButton component="a" href={link[currentImageIndex]} onClick={() => {}}>
                                    <Launch sx={{ fontSize: { xs: "18px", sm: "24px" }, color: "#C2410C" }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        {!isGuest && (
                            <Tooltip title="Like" placement="right">
                                <IconButton onClick={() => handleLikeImage()}>
                                    {imageLikes[currentImageIndex].includes(authState.account!.username) ? (
                                        <Favorite sx={{ fontSize: { xs: "18px", sm: "24px" }, color: "#C2410C" }} />
                                    ) : (
                                        <FavoriteBorder sx={{ fontSize: { xs: "18px", sm: "24px" }, color: "#C2410C" }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title="Comment" placement="right">
                            <IconButton
                                onClick={() => setOpenComments(!openComments)}
                                sx={{
                                    backgroundColor: openComments ? lighten(theme.palette.primary.main, 0.7) : "transparent",
                                    "&:hover": {
                                        backgroundColor: openComments ? lighten(theme.palette.primary.main, 0.7) : "rgba(0,0,0,0.05)", // default hover when not active
                                    },
                                    "&:active": {
                                        backgroundColor: lighten(theme.palette.primary.main, 0.7), // ensure active touch state shows color
                                    },
                                }}
                            >
                                <Comment sx={{ fontSize: { xs: "18px", sm: "24px" }, color: "#C2410C" }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Captions" placement="right">
                            <IconButton
                                disabled={!images[currentImageIndex].caption}
                                onClick={() => setOpenCaptions(!openCaptions)}
                                sx={{
                                    backgroundColor: openCaptions ? lighten(theme.palette.primary.main, 0.7) : "transparent",
                                    "&:hover": {
                                        backgroundColor: openCaptions ? lighten(theme.palette.primary.main, 0.7) : "rgba(0,0,0,0.05)",
                                    },
                                    "&:active": {
                                        backgroundColor: lighten(theme.palette.primary.main, 0.7),
                                    },
                                }}
                            >
                                <Subtitles
                                    sx={{
                                        fontSize: { xs: "18px", sm: "24px" },
                                        color: !!images[currentImageIndex].caption ? "#C2410C" : lighten(theme.palette.text.secondary, 0.5),
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {/* Captions Box */}
                    <Slide in={openCaptions} direction="up" timeout={{ enter: 250, exit: 200 }} mountOnEnter unmountOnExit>
                        <Box
                            sx={{
                                position: "absolute",
                                width: "100%",
                                left: { sm: 5 },
                                bottom: 0,
                                p: 1,
                                border: "2px solid " + theme.palette.primary.main,
                                borderTopLeftRadius: 5,
                                borderTopRightRadius: 5,
                                maxHeight: 300,
                                display: "flex",
                                flexDirection: { xs: "row", sm: "column" },
                                backgroundColor: "rgba(255, 255, 255, 0.51)",
                                backdropFilter: "blur(40px)",
                                WebkitBackdropFilter: "blur(40px)",
                                borderColor: "rgba(255, 255, 255, 0.4)",
                                willChange: "transform",
                                pointerEvents: openCaptions ? "auto" : "none",
                            }}
                        >
                            <Typography sx={{ color: "#C2410C" }}>{images[currentImageIndex]?.caption}</Typography>
                        </Box>
                    </Slide>
                </Box>
                <Divider orientation="vertical" />

                {/* Comment box with fixed height matching image */}
                {openComments && (
                    <Box
                        sx={{
                            width: { xs: "100%", sm: 300 },
                            flexShrink: 0,
                            minWidth: 0,
                            height: "100%",
                            overflowY: "auto",
                            backgroundColor: "#f9f9f9",
                            boxSizing: "border-box",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ display: "flex", backgroundColor: "#fff", py: 2, alignItems: "center" }}>
                            <IconButton sx={{ display: { xs: "flex", sm: "none" }, ml: 1 }} onClick={() => setOpenComments(false)}>
                                <ArrowBack />
                            </IconButton>
                            <Avatar src={images[currentImageIndex].user.image?.url} sx={{ ml: 2, mr: 1 }} />
                            <Typography variant="subtitle1" fontWeight="bold">
                                {images[currentImageIndex].user.username}
                            </Typography>
                        </Box>
                        <Divider />

                        {/* Main content (chat history or filler) */}
                        <Box sx={{ flexGrow: 1, padding: 2, height: "calc(80dvh - 225px)", overflow: "auto", ...scrollbarCss }}>
                            <Comments comments={images[currentImageIndex].comments} setData={setImages} type="ImageBox" />
                        </Box>

                        {/* Chat input */}
                        <Box
                            sx={{
                                padding: 2,
                                borderTop: "1px solid #ddd",
                                display: "flex",
                                alignItems: "flex-end",
                                gap: 1,
                                backgroundColor: "#fff",
                            }}
                        >
                            <TextField
                                disabled={!authState.account}
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder={!!authState.account ? "Write a comment..." : "Log in to comment!"}
                                variant="outlined"
                                size="small"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                sx={{
                                    flexGrow: 1,
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 2,
                                }}
                            />
                            <IconButton color="primary" onClick={handleSendMessage} disabled={!message.trim()}>
                                <Send />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ImageBox;
